import React, { useRef, useEffect } from 'react';

// DVD-screensaver-style floating logo. Drifts slowly across its parent
// container and bounces off the edges. Purely decorative.
export default function FloatingLogo({ src, size = 200, speed = 0.55, vary = false, className = '' }) {
  const boxRef = useRef(null);
  const imgRef = useRef(null);
  const rafRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    const box = boxRef.current;
    const el = imgRef.current;
    if (!box || !el) return;

    // Unit directions: horizontal, vertical and diagonal.
    const D = 0.7071;
    const dirs = [
      [1, 0], [-1, 0], [0, 1], [0, -1],
      [D, D], [-D, D], [D, -D], [-D, -D],
    ];

    let w = box.clientWidth;
    let h = box.clientHeight;
    let x = Math.random() * Math.max(1, w - size);
    let y = Math.random() * Math.max(1, h - size);
    const start = dirs[Math.floor(Math.random() * dirs.length)];
    let vx = start[0] * speed;
    let vy = start[1] * speed;

    const step = () => {
      w = box.clientWidth;
      h = box.clientHeight;
      x += vx;
      y += vy;
      if (x <= 0) { x = 0; vx = Math.abs(vx); }
      if (x >= w - size) { x = w - size; vx = -Math.abs(vx); }
      if (y <= 0) { y = 0; vy = Math.abs(vy); }
      if (y >= h - size) { y = h - size; vy = -Math.abs(vy); }
      el.style.transform = `translate(${x}px, ${y}px)`;
      rafRef.current = requestAnimationFrame(step);
    };
    step();

    // Periodically choose a fresh horizontal / vertical / diagonal path.
    if (vary) {
      intervalRef.current = setInterval(() => {
        const d = dirs[Math.floor(Math.random() * dirs.length)];
        vx = d[0] * speed;
        vy = d[1] * speed;
      }, 4200);
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [size, speed, vary]);

  return (
    <div ref={boxRef} className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
      <img
        ref={imgRef}
        src={src}
        alt=""
        style={{ width: size, willChange: 'transform' }}
        className="absolute top-0 left-0 select-none opacity-90 drop-shadow-[0_10px_40px_rgba(0,0,0,0.45)]"
      />
    </div>
  );
}
